import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowUp, CircleStop, Download, LockKeyhole, Mic, Pause, Play, RotateCcw, Send, Trash2, X } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { audioFilename } from "../lib/audio";
import useVoiceRecording from "../lib/useVoiceRecording";
import VoiceWaveform from "./VoiceWaveform";
import VoiceDiscardAnimation from "./VoiceDiscardAnimation";
import { VOICE_PHASE as P, VOICE_LOCK_DISTANCE, voiceClock, voiceGesture } from "../lib/voiceGesture";
import "../voice-recorder.css";

export default function VoiceRecorder({ onSend, disabled = false, sendLabel, gesture = false, binding,
  contextTitle, floating = false, triggerProps = {}, onActivityChange }) {
  const { t } = useI18n();
  const audio = useVoiceRecording({ onSend, binding, t, gesture });
  const pointer = useRef(null); const [lockProgress, setLockProgress] = useState(0);
  const controlPress = useRef(null);
  const { phase, recording, error } = audio;
  const held = phase === P.RECORDING_HELD;
  const locked = [P.RECORDING_LOCKED, P.PAUSED_LOCKED].includes(phase);
  const pending = phase === P.PERMISSION_PENDING;
  const busy = [P.SENDING, P.PROCESSING].includes(phase);
  const active = pending || held || locked || busy || Boolean(recording);
  const lastVisual = useRef(null);
  const panelRef = useRef(null);
  if (pending) lastVisual.current = null;
  if (held || locked) lastVisual.current = { ...lastVisual.current, waveform: audio.waveform, level: audio.level, elapsed: audio.elapsed, held, title: audio.binding?.displayTitle || audio.binding?.context?.title };
  const [discardFinished, setDiscardFinished] = useState(false);
  const discarding = gesture && phase === P.CANCELLED && !discardFinished;
  const discardLocked = discarding && lastVisual.current?.held === false;
  const discardHeld = discarding && !discardLocked;
  useEffect(() => {
    if (phase !== P.CANCELLED) { setDiscardFinished(false); return undefined; }
    // Visual lifetime only; leave the existing CANCELLED -> IDLE transition alone.
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const timer = setTimeout(() => setDiscardFinished(true), reducedMotion ? 160 : 850);
    return () => clearTimeout(timer);
  }, [phase]);
  const visuallyActive = active || discarding;
  const activityRef = useRef(onActivityChange); activityRef.current = onActivityChange;
  useEffect(() => { activityRef.current?.(visuallyActive); }, [visuallyActive]);
  useEffect(() => () => { activityRef.current?.(false); }, []);
  const capturedTitle = audio.binding?.displayTitle || audio.binding?.context?.title;
  const shownTitle = active || recording ? capturedTitle : contextTitle;
  const hasLevel = audio.level !== null;
  useLayoutEffect(() => {
    if (!gesture || (!locked && !held)) return undefined;
    const panel = panelRef.current;
    const measure = () => {
      const wave = panel?.querySelector('[data-testid="voice-wave"]')?.getBoundingClientRect();
      const bounds = panel?.getBoundingClientRect();
      if (!wave || !bounds || !lastVisual.current) return;
      lastVisual.current.geometry = {
        x: wave.x - bounds.x, y: wave.y - bounds.y,
        width: wave.width, height: wave.height,
      };
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (panel) observer?.observe(panel);
    return () => observer?.disconnect();
  }, [gesture, locked, held, hasLevel, shownTitle]);
  const detach = () => {
    const heldPointer = pointer.current; pointer.current = null; setLockProgress(0);
    try { heldPointer?.target.releasePointerCapture?.(heldPointer.id); } catch (_) { /* already released */ }
  };
  useEffect(() => {
    if ([P.IDLE, P.FAILED, P.CANCELLED].includes(phase)) {
      const captured = pointer.current; pointer.current = null; setLockProgress(0);
      try { captured?.target.releasePointerCapture?.(captured.id); } catch (_) { /* already released */ }
    }
  }, [phase]);
  const cancelGesture = () => {
    if (!pointer.current) return;
    detach();
    if ([P.RECORDING_HELD, P.PERMISSION_PENDING].includes(audio.phaseRef.current)) audio.cancel();
  };
  const down = (event) => {
    if (disabled || event.button > 0 || event.isPrimary === false || pointer.current || active || recording) return;
    event.preventDefault();
    pointer.current = { id: event.pointerId, x: event.clientX, y: event.clientY, target: event.currentTarget };
    try { event.currentTarget.setPointerCapture(event.pointerId); }
    catch (_) { pointer.current = null; return; } // No hold without a reliable release.
    audio.start(true);
  };
  const move = (event) => {
    if (!pointer.current || pointer.current.locked || pointer.current.id !== event.pointerId || ![P.PERMISSION_PENDING, P.RECORDING_HELD].includes(audio.phaseRef.current)) return;
    event.preventDefault();
    const point = { x: event.clientX, y: event.clientY }; const result = voiceGesture(pointer.current, point);
    setLockProgress(Math.min(1, Math.max(0, pointer.current.y - point.y) / VOICE_LOCK_DISTANCE));
    if (result === "cancel") { detach(); audio.cancel(true); }
    else if (result === "lock") { pointer.current.locked = true; audio.lock(); }
  };
  const up = (event) => {
    if (!pointer.current || pointer.current.id !== event.pointerId) return;
    event.preventDefault();
    const intent = pointer.current.locked ? "lock" : voiceGesture(pointer.current, { x: event.clientX, y: event.clientY });
    // The last touch position can arrive only with pointerup (coalesced moves).
    // Resolve its intent before release-to-send; pending permission still cancels.
    if (audio.phaseRef.current === P.PERMISSION_PENDING) audio.cancel();
    else if (audio.phaseRef.current === P.RECORDING_HELD) {
      if (intent === "lock") audio.lock();
      else if (intent === "cancel") audio.cancel(true);
      else audio.finish(true);
    }
    detach();
  };
  const triggerHidden = discarding || (active && !pending && !held);
  const trigger = <button {...triggerProps} type="button"
    className={`${triggerProps.className || "w-11 h-11 shrink-0 rounded-full grid place-items-center"} pc-voice-trigger`}
    style={{ background: "var(--pc-cyan)", color: "var(--pc-player-on-accent, #001014)", ...triggerProps.style,
      visibility: triggerHidden && !pointer.current ? "hidden" : undefined,
      opacity: triggerHidden && pointer.current ? 0 : triggerProps.style?.opacity }}
    disabled={disabled || busy} aria-label={t("voiceHoldToRecord")} title={t("voiceHoldToRecord")}
    aria-hidden={triggerHidden || undefined} tabIndex={triggerHidden ? -1 : 0}
    data-testid={triggerProps["data-testid"] || "voice-start"} data-phase={phase}
    onPointerDown={down} onPointerMove={move} onPointerUp={up}
    onPointerCancel={cancelGesture} onLostPointerCapture={cancelGesture} onTouchCancel={cancelGesture}
    onContextMenu={(event) => event.preventDefault()} onDragStart={(event) => event.preventDefault()}
    onKeyDown={(event) => {
      if (event.key === "Escape") { event.preventDefault(); audio.cancel(); return; }
      if ([" ", "Enter"].includes(event.key) && !event.repeat && !active) { event.preventDefault(); audio.start(false); }
    }}
    // Pointer clicks never start capture. Keyboard activation explicitly starts locked.
    onClick={(event) => event.preventDefault()}
  >{triggerProps.children || <Mic size={floating ? 19 : 18} />}</button>;

  // A gesture ending over newly revealed controls is not a new Send tap.
  // Pointer controls require their own press; keyboard/assistive clicks still work.
  const explicitControl = (action) => ({
    onPointerDown: (event) => { controlPress.current = !pointer.current && event.button === 0 ? event.currentTarget : null; },
    onPointerCancel: () => { controlPress.current = null; },
    onClick: (event) => {
      const intentional = !pointer.current && (event.detail === 0 || controlPress.current === event.currentTarget);
      controlPress.current = null;
      if (intentional) action();
    },
  });
  const controls = <div className="pc-voice-controls" data-discard={discardLocked || undefined}>
    <button type="button" className="pc-voice-control" disabled={discarding} aria-hidden={discarding || undefined} tabIndex={discarding ? -1 : undefined} aria-label={t("voiceDiscard")} {...explicitControl(() => audio.cancel())} data-testid="voice-delete"><Trash2 size={20} /></button>
    {locked && audio.canPause && <button type="button" className="pc-voice-control" {...explicitControl(audio.pause)} aria-label={t(phase === P.PAUSED_LOCKED ? "voiceResume" : "voicePause")} data-testid="voice-pause">
      {phase === P.PAUSED_LOCKED ? <Play size={20} /> : <Pause size={20} />}
    </button>}
    {!discarding && <button type="button" className="pc-voice-control pc-voice-control--send" {...explicitControl(() => locked ? audio.finish(true) : audio.send())} data-testid="voice-send" aria-label={sendLabel || t("send")}><Send size={20} /></button>}
  </div>;
  const errorPanel = error && <div className="pc-voice-error" data-testid="voice-error">
    <p role="alert">{error}</p>
    {recording && <><p>{t("voiceSendUncertain")}</p><audio controls src={recording.url} preload="metadata" />
      <a href={recording.url} download={audioFilename("voice-note", recording.type)} className="pc-voice-download"><Download size={16} />{t("voiceSaveLocal")}</a></>}
    <button type="button" className="pc-voice-control" onClick={audio.reset} aria-label={t("close")}><X size={18} /></button>
  </div>;

  if (!gesture) return <div data-testid="voice-recorder" data-phase={phase}>
    {![P.SENDING, P.PROCESSING, P.PERMISSION_PENDING, P.RECORDING_LOCKED, P.PAUSED_LOCKED, P.PREVIEW].includes(phase) && !recording &&
      <button type="button" disabled={disabled} className="pc-btn pc-btn-primary flex items-center gap-2" onClick={() => audio.start(false)} data-testid="voice-start"><Mic size={17} />{t("startRecording")}</button>}
    {locked && <div className="rounded-xl p-4" style={{ background: "var(--pc-charcoal-2)" }}>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm"><span className="w-2 h-2 rounded-full" style={{ background: "#ff6b6b" }} />{t("recording")} {voiceClock(audio.elapsed)}</span>
        <button type="button" onClick={() => audio.finish(false)} className="pc-btn pc-btn-ghost flex items-center gap-2" data-testid="voice-stop"><CircleStop size={16} />{t("stop")}</button>
      </div>
    </div>}
    {(pending || busy) && <p role="status">{t(pending ? "voicePermissionPending" : phase === P.SENDING ? "sending" : "voiceProcessing")}</p>}
    {phase === P.PREVIEW && recording && <div className="rounded-xl p-4" style={{ background: "var(--pc-charcoal-2)" }} data-testid="voice-preview">
      <p className="text-xs mb-2" style={{ color: "var(--pc-muted)" }}>{t("listenBeforeSend")} · {voiceClock(recording.duration)}</p>
      <audio controls preload="metadata" src={recording.url} className="w-full" data-testid="voice-preview-audio" />
      <div className="flex flex-wrap gap-2 mt-3">
        <button type="button" onClick={() => audio.start(false)} className="pc-btn pc-btn-ghost flex items-center gap-2"><RotateCcw size={15} />{t("recordAgain")}</button>
        <button type="button" onClick={audio.reset} className="pc-btn pc-btn-ghost flex items-center gap-2"><Trash2 size={15} />{t("cancel")}</button>
        <button type="button" onClick={audio.send} className="pc-btn pc-btn-primary flex items-center gap-2" data-testid="voice-send"><Send size={15} />{sendLabel || t("send")}</button>
      </div>
    </div>}
    {errorPanel}
  </div>;

  const panel = (active || error || discarding) && <section ref={panelRef} className={`pc-voice-panel ${floating ? "pc-voice-panel--floating" : "pc-voice-panel--composer"}`}
    style={{ ...(floating ? { position: triggerProps.style?.position || "fixed" } : {}) }}
    data-testid="voice-panel" data-phase={phase} data-held={held || pending || (discarding && lastVisual.current?.held) || undefined} data-inactive={!visuallyActive || undefined}>
    {(discarding ? lastVisual.current?.title : shownTitle) && <p className={`pc-voice-context ${discarding ? "pc-voice-discard-spacer" : ""}`} title={shownTitle} data-testid="voice-recording-context" aria-hidden={discarding || undefined}>{t("voiceAbout")} {discarding ? lastVisual.current?.title : shownTitle}</p>}
    {(held || locked || discarding) && <><div className="pc-voice-live">
      <span className="pc-voice-dot" data-paused={phase === P.PAUSED_LOCKED || undefined} />
      <span className="pc-voice-time" data-testid="voice-timer">{voiceClock(discarding ? lastVisual.current?.elapsed || 0 : audio.elapsed)}</span>
      {discarding ? <VoiceDiscardAnimation locked={discardLocked} source={lastVisual.current?.geometry} waveform={lastVisual.current?.waveform} level={lastVisual.current?.level} label={t("voiceCancelled")} /> : audio.level !== null ? <VoiceWaveform waveform={audio.waveform} level={audio.level} paused={phase === P.PAUSED_LOCKED} label={t("voiceLevel")} /> : <span className="pc-voice-state">{t("recording")}</span>}
      {(locked || discardLocked) && <LockKeyhole className="pc-voice-lock-icon" size={20} strokeWidth={2.5} aria-label={t("voiceLocked")} />}
    </div>
      {held && <><p className="pc-voice-swipe"><ArrowLeft size={14} />{t("voiceSwipeCancel")}</p>
        <div className="pc-voice-lock-hint" style={{ "--pc-lock-progress": lockProgress }} data-testid="voice-lock-hint"><LockKeyhole className="pc-voice-lock-icon" size={22} strokeWidth={2.5} /><ArrowUp size={15} /><span>{t("voiceSwipeLock")}</span></div></>}
      {discardHeld && <div className="pc-voice-swipe pc-voice-discard-spacer" aria-hidden="true" style={{ height: 18 }} />}
      {(locked || discardLocked) && controls}
    </>}
    {pending && <p className="pc-voice-state" role="status">{t("voicePermissionPending")}</p>}
    {busy && <p className="pc-voice-state" role="status">{t(phase === P.SENDING ? "sending" : "voiceProcessing")}</p>}
    {phase === P.PREVIEW && recording && <div data-testid="voice-preview">
      <p>{audio.reachedLimit ? t("voiceLimitReached") : t("listenBeforeSend")} · {voiceClock(recording.duration)}</p>
      <audio controls src={recording.url} className="w-full" />{controls}
    </div>}
    {errorPanel}
  </section>;
  // Keep the captured trigger mounted at the same coordinates throughout the hold.
  return <>{trigger}{panel}</>;
}
